import React, { useState, useContext, useEffect } from 'react';
import { UserContext } from './App';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaWallet, FaBan, FaHistory } from 'react-icons/fa';
import axios from 'axios';
import './DepositForm.css';

const WithdrawForm = ({ isOpen = true, onClose }) => {
  const navigate = useNavigate();
  const { user, setUser, activeBalance, setActiveBalance, setIsDepositModalOpen } = useContext(UserContext);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('BTC');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [showHistory, setShowHistory] = useState(false);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const saved = localStorage.getItem('withdrawTransactions');
      if (saved) setTransactions(JSON.parse(saved));
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const cryptoCurrencies = [
    { value: 'BTC',  name: 'Bitcoin',         sub: '',       img: '/Bitcoin.png' },
    { value: 'ETH',  name: 'Ethereum',        sub: '(ERC20)',img: '/ethereum.png' },
    { value: 'LTC',  name: 'Litecoin',        sub: '',       img: '/litecoin.png' },
    { value: 'SOL',  name: 'Solana',          sub: '',       img: 'https://cryptologos.cc/logos/solana-sol-logo.png' },
    { value: 'USDT', name: 'USDT',            sub: '(ERC20)',img: '/tether.png' },
  ];

  const handleWithdraw = async (e) => {
    if (e) e.preventDefault();
    const currentBal = activeBalance === 'promo' ? (user?.promoBalance || 0) : (user?.balance || 0);

    const minAmount = activeBalance === 'promo' ? 50 : 35;

    if (!amount || parseFloat(amount) < minAmount) {
      setMessage({ text: `Minimum withdrawal is $${minAmount}`, type: 'error' }); return;
    }
    if (!address) {
      setMessage({ text: 'Please enter a wallet address', type: 'error' }); return;
    }
    if (parseFloat(amount) > currentBal) {
      setMessage({ text: 'Insufficient balance', type: 'error' }); return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        'https://test3-0ar4.onrender.com/api/payment/withdraw',
        { amount: parseFloat(amount), currency, address, usePromoBalance: activeBalance === 'promo' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message) {
        setMessage({ text: 'Withdrawal successfully requested!', type: 'success' });
        
        const tx = {
          id: Date.now().toString(),
          date: new Date().toISOString(),
          currency,
          amount: parseFloat(amount),
          status: 'pending'
        };
        const newTxs = [tx, ...transactions].slice(0, 5);
        setTransactions(newTxs);
        localStorage.setItem('withdrawTransactions', JSON.stringify(newTxs));

        if (user && setUser) {
          setUser(prev => ({ 
            ...prev, 
            ...(activeBalance === 'promo' 
              ? { promoBalance: prev.promoBalance - parseFloat(amount) } 
              : { balance: prev.balance - parseFloat(amount) }
            ) 
          }));
        }
        setAmount('');
        setAddress('');
      } else {
        setMessage({ text: 'Failed to request withdrawal.', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: err.response?.data?.error || 'Failed to process withdrawal.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    else navigate('/dashboard');
  };

  if (!isOpen) return null;

  const currentBal = activeBalance === 'promo' ? (user?.promoBalance || 0) : (user?.balance || 0);

  return (
    <div className="dp-overlay" onClick={handleClose}>
      <div className="dp-modal" onClick={e => e.stopPropagation()}>

        {/* ── Top action bar ── */}
        <div className="dp-topbar">
          <div className="dp-topbar-left">
            <button 
              className="dp-tab"
              onClick={() => {
                handleClose();
                if (setIsDepositModalOpen) setIsDepositModalOpen(true);
              }}
            >
              + Deposit
            </button>
            <button className="dp-tab dp-tab-active">
              ↑ Withdraw
            </button>
          </div>
          <div className="dp-topbar-right">
            <div className="dp-balance-switcher">
              <button 
                className={`dp-bal-toggle ${activeBalance === 'balance' ? 'active' : ''}`}
                onClick={() => setActiveBalance('balance')}
              >
                Normal: ${(user?.balance || 0).toFixed(2)}
              </button>
              <button 
                className={`dp-bal-toggle ${activeBalance === 'promo' ? 'active' : ''}`}
                onClick={() => setActiveBalance('promo')}
              >
                Promo: ${(user?.promoBalance || 0).toFixed(2)}
              </button>
            </div>
            <button className="dp-close-btn" onClick={handleClose}>
              <FaTimes />
            </button>
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="dp-content">
          <div className="dp-left">
            {message && (
              <div className={`dp-alert ${message.type}`}>
                {message.type === 'error' ? '' : ''}{message.text}
              </div>
            )}

            {activeBalance === 'promo' && (
              <div className="dp-alert warning" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#ffb703', borderColor: 'rgba(245, 158, 11, 0.2)', marginBottom: '20px' }}>
                Withdrawals from promo balance carry a 20% fee.
              </div>
            )}

            {/* Steam Skins (Disabled) */}
            <div className="dp-section-title">Steam Skins</div>
            <div className="dp-grid dp-grid-2">
              <div className="dp-card dp-disabled">
                <div className="dp-disabled-overlay">
                  <FaBan className="dp-ban-icon" />
                  <span>Not Available</span>
                </div>
                <div className="dp-card-img dp-card-img-rust">
                  <img src="https://upload.wikimedia.org/wikipedia/en/thumb/e/e4/Rust_game_logo.png/250px-Rust_game_logo.png" alt="Rust" />
                </div>
                <div className="dp-card-label">Rust</div>
              </div>
              <div className="dp-card dp-disabled">
                <div className="dp-disabled-overlay">
                  <FaBan className="dp-ban-icon" />
                  <span>Not Available</span>
                </div>
                <div className="dp-card-img dp-card-img-cs2">
                  <img src="https://upload.wikimedia.org/wikipedia/en/f/f2/CS2_logo.png" alt="CS2" />
                </div>
                <div className="dp-card-label">CS2</div>
              </div>
            </div>

            {/* Real Cash (Disabled) */}
            <div className="dp-section-title">Real Cash</div>
            <div className="dp-grid dp-grid-3">
              <div className="dp-card dp-disabled">
                <div className="dp-disabled-overlay">
                  <FaBan className="dp-ban-icon" />
                  <span>Deposit Only</span>
                </div>
                <div className="dp-card-img">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/320px-Visa_Inc._logo.svg.png" alt="Visa" style={{ width: 60 }} />
                </div>
                <div className="dp-card-label">Credit Card</div>
              </div>
              <div className="dp-card dp-disabled">
                <div className="dp-disabled-overlay">
                  <FaBan className="dp-ban-icon" />
                  <span>Deposit Only</span>
                </div>
                <div className="dp-card-img">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/320px-PayPal.svg.png" alt="PayPal" style={{ width: 80 }} />
                </div>
                <div className="dp-card-label">PayPal</div>
              </div>
            </div>

            {/* Crypto Currencies (Active) */}
            <div className="dp-section-title">Crypto Currencies</div>
            <div className="dp-grid dp-grid-5">
              {cryptoCurrencies.map(c => (
                <div
                  key={c.value}
                  className={`dp-card dp-crypto-card ${currency === c.value ? 'dp-card-active' : ''}`}
                  onClick={() => setCurrency(c.value)}
                >
                  <div className="dp-crypto-icon">
                    <img src={c.img} alt={c.name} />
                  </div>
                  <div className="dp-card-label">
                    {c.name} {c.sub && <span className="dp-sub">{c.sub}</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Withdrawal form fields */}
            <form onSubmit={handleWithdraw} className="dp-amount-form">
              <div className="dp-input-group" style={{ marginBottom: '20px' }}>
                <div className="dp-section-title" style={{ fontSize: '12px', opacity: 0.7, marginBottom: '8px' }}>Destination {currency} Address</div>
                <div className="dp-address-input-wrap">
                  <FaWallet className="dp-input-icon" />
                  <input
                    type="text"
                    className="dp-address-input"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder={`Enter your ${currency} wallet address`}
                    required
                  />
                  <button 
                    type="button" 
                    className="dp-paste-btn"
                    onClick={async () => {
                      try {
                        const text = await navigator.clipboard.readText();
                        setAddress(text);
                      } catch(e) {}
                    }}
                  >
                    PASTE
                  </button>
                </div>
              </div>

              <div className="dp-amount-row">
                <div className="dp-amount-input-wrap">
                  <span className="dp-prefix">$</span>
                  <input
                    type="number"
                    className="dp-amount-input"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder="0.00"
                    min={activeBalance === 'promo' ? 50 : 35}
                    max={currentBal}
                    step="0.01"
                    required
                  />
                  <button 
                    type="button" 
                    className="dp-max-btn"
                    onClick={() => setAmount(currentBal.toString())}
                  >
                    MAX
                  </button>
                </div>
                <div className="dp-quick-row">
                  {(activeBalance === 'promo' ? [50, 100, 250, 500] : [35, 50, 100, 250]).map(val => (
                    <button key={val} type="button" className="dp-quick-btn" onClick={() => setAmount(val.toString())}>
                      ${val}
                    </button>
                  ))}
                </div>
              </div>

              {activeBalance === 'balance' && user?.unwageredAmount > 0 && (
                <div className="dp-alert warning" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.2)', marginBottom: '15px', fontSize: '12px' }}>
                  <FaBan style={{ marginRight: 8 }} />
                  You must wager ${user.unwageredAmount.toFixed(2)} more before you can withdraw from your normal balance.
                </div>
              )}

              <button 
                type="submit" 
                className="dp-submit-btn dp-submit-withdraw" 
                disabled={loading || !amount || parseFloat(amount) < (activeBalance === 'promo' ? 50 : 35) || parseFloat(amount) > currentBal || !address || (activeBalance === 'balance' && user?.unwageredAmount > 0)}
                style={{ 
                  background: (activeBalance === 'balance' && user?.unwageredAmount > 0) ? '#333' : 'linear-gradient(135deg, #ffb703, #d97706)',
                  cursor: (activeBalance === 'balance' && user?.unwageredAmount > 0) ? 'not-allowed' : 'pointer',
                  opacity: (activeBalance === 'balance' && user?.unwageredAmount > 0) ? 0.5 : 1
                }}
              >
                {loading ? 'Processing...' : (activeBalance === 'balance' && user?.unwageredAmount > 0) ? 'Wagering Required' : `Withdraw $${parseFloat(amount || 0).toFixed(2)} via ${currency}`}
              </button>
            </form>
          </div>

          {/* ── Right: History ── */}
          <div className="dp-right">
            <div className="dp-affiliate-box dp-history-box" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <div className="dp-affiliate-title">
                <FaHistory style={{ marginRight: 8 }} /> Recent Withdrawals
              </div>
              <div className="dp-history-list">
                {transactions.length === 0 ? (
                  <div className="dp-no-history">No recent withdrawals.</div>
                ) : (
                  transactions.map(tx => (
                    <div key={tx.id} className="dp-history-item">
                      <div className="dp-hist-left">
                        <div className="dp-hist-name">{tx.currency} Withdraw</div>
                        <div className="dp-hist-time">{new Date(tx.date).toLocaleDateString()}</div>
                      </div>
                      <div className="dp-hist-right">
                        <div className="dp-hist-val">-${tx.amount.toFixed(2)}</div>
                        <div className={`dp-hist-status dp-stat-${tx.status.toLowerCase()}`}>{tx.status}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawForm;

